/**
 * Store page types (referral-code demo, 2026-11-04). A store is a COUPON store (/{slug}/coupons, every store today) or a REFERRAL
 * store (/{slug}/referral-code, crypto exchanges that hand out a referral code). Only the TEXT of the page differs: the offers,
 * the code reveal flow and /out/{id} are the same for both. Pure, no I/O: unit-tested.
 *
 * Keyword rules (SEO template 2026-11-04, section 3): "referral code" is the main term, "promo code" the secondary one and
 * "coupon" the third. The URL, the offer list heading, the how-to heading and the image alt text say only "referral code";
 * the title and H1 add "promo code"; one H2 holds both secondary terms and answers it directly. No code in any text.
 */
import { safeMetaDescription } from "@/lib/meta-text";

export type PageType = "COUPON" | "REFERRAL";

export const REFERRAL_SEGMENT = "referral-code";
export const COUPONS_SEGMENT = "coupons";

/** The page type of a store row; anything but "REFERRAL" (null, missing column, a typo) is a COUPON store. */
export function pageTypeOf(store: { pageType?: string | null } | null | undefined): PageType {
    return store?.pageType === "REFERRAL" ? "REFERRAL" : "COUPON";
}

export const isReferralStore = (store: { pageType?: string | null } | null | undefined): boolean => pageTypeOf(store) === "REFERRAL";

/** The route segment that serves the store: "coupons" or "referral-code". */
export const pageSegmentOf = (store: { pageType?: string | null } | null | undefined): string =>
    isReferralStore(store) ? REFERRAL_SEGMENT : COUPONS_SEGMENT;

/** Site-relative path of the store page. */
export const storePagePath = (store: { slug: string; pageType?: string | null }): string => `/${store.slug}/${pageSegmentOf(store)}`;

/** `attributes` with the `page_type` mirror set for a REFERRAL store and removed for any other, so links can read it from the JSON they already load. */
export function withPageTypeAttribute(attributes: unknown, pageType: unknown): Record<string, unknown> {
    const result = typeof attributes === "object" && attributes !== null && !Array.isArray(attributes) ? { ...(attributes as Record<string, unknown>) } : {};
    if (pageType === "REFERRAL") result.page_type = "REFERRAL";
    else delete result.page_type;
    return result;
}

/** Shown when the owner tries to switch own-codes-only off on a REFERRAL store. */
export const REFERRAL_OWN_CODES_MESSAGE = "A referral-code store lists only your own codes: own-codes-only cannot be switched off while the page type is REFERRAL.";

/**
 * The rule that a REFERRAL store is always an own-codes-only store (owner 2026-11-04: referral-code companies get no scraping, only the
 * owner's codes). True when the pair breaks it. Enforced in the database too (CHECK constraint "Store_referral_own_codes_only_check").
 */
export const referralNeedsOwnCodes = (store: { pageType?: string | null } | null | undefined, ownCodesOnly: boolean | null | undefined): boolean =>
    isReferralStore(store) && ownCodesOnly !== true;

/**
 * The flat root category of the referral-code stores ("Crypto", owner 2026-11-04). Its page lives at /crypto-referral-codes, not
 * under /{slug}/coupons: the slug `crypto` belongs to the Crypto.com store (lib/slug-guard), and the page lists referral codes, not coupons.
 */
export const REFERRAL_CATEGORY_SLUG = "crypto-referral-codes";
export const isReferralCategorySlug = (slug: string | null | undefined): boolean => slug === REFERRAL_CATEGORY_SLUG;

/** Site-relative path of a root category's page: /crypto-referral-codes for the referral category, /{slug}/coupons for every other. */
export const categoryPagePath = (slug: string): string => (isReferralCategorySlug(slug) ? `/${REFERRAL_CATEGORY_SLUG}` : `/${slug}/${COUPONS_SEGMENT}`);

/** Texts of the referral category page (SEO template section 4): referral codes only, no coupon count, nothing about how we check. */
export function referralCategoryCopy(name: string, providers: number, topStores: string[]) {
    const lower = name.toLowerCase();
    const brands = `${providers} ${lower} ${providers === 1 ? "brand" : "brands"}`;
    const including = topStores.length > 0 ? `, including ${topStores.join(", ")}` : "";
    return {
        title: `${name} Referral Codes for Exchanges and Platforms`,
        h1: `${name} Referral Codes`,
        h1Accent: "Referral Codes",
        lead: `Referral codes for ${brands}, with where to enter each one when you sign up.`,
        metaDescription: safeMetaDescription(`Referral codes for ${brands}${including}. Open a brand page to get its code and see where to enter it at signup.`),
        offersTitle: `Top ${name} Referral Codes`,
        browseTitle: `Browse ${name} Brands`,
        tileLabel: "referral codes",
        tileSuffix: "referral code",
        collectionName: `${name} Referral Codes`,
    };
}

const article = (name: string) => (/^[aeiou]/i.test(name.trim()) ? "an" : "a");

export interface ReferralCopy {
    title: string;
    h1: string;
    metaDescription: string;
    listHeading: string;
    expiredHeading: string;
    aboutHeading: string;
    faqHeading: string;
    answerHeading: string;
    answerText: string;
    howToHeading: string;
    howToIntro: string;
    steps: Array<{ heading: string; text: string }>;
    imageAlt: string;
    snapshotTitle: string;
    snapshotSubtitle: string;
    signupTitle: string;
    highlightsTitle: string;
    sidebarSummary: string;
    modalInstruction: string;
    modalVoteQuestion: string;
}

/** Every public text of a REFERRAL page, for one store name. No code, no bonus, nothing about how we check. */
export function referralCopy(name: string, lateAdd?: string | null): ReferralCopy {
    return {
        title: `${name} Referral Code & Promo Code`,
        h1: `${name} Referral Code & Promo Code`,
        metaDescription: safeMetaDescription(
            `The ${name} referral code goes in the signup form before your account exists. Here it is, and what to know about a ${name} promo code or coupon.`,
        ),
        listHeading: `Active ${name} referral code`,
        expiredHeading: `Recently expired ${name} referral codes`,
        aboutHeading: `About ${name}`,
        faqHeading: `${name} Referral Code FAQ`,
        answerHeading: `Is there a ${name} coupon or promo code?`,
        answerText: `On this page the ${name} offer is a referral code, not a coupon. It goes in the signup form when you create your account, and it is in the list above.`,
        howToHeading: `How to use ${article(name)} ${name} referral code`,
        howToIntro: `The code goes in during signup, before your ${name} account is created. Copy it here, then paste it in the signup form.`,
        steps: [
            { heading: "Find the code", text: `The ${name} code is in the list above.` },
            { heading: "Reveal and copy it", text: `Click "Get Code". A new tab opens with a popup showing the code, and your first tab moves on to ${name}. Click the copy icon to copy it.` },
            { heading: "Paste it during signup", text: `On ${name}'s signup form, paste the code in the referral-code field, before you create your account. ${lateAdd?.trim() || "It usually can't be added afterwards."}` },
        ],
        imageAlt: `Where to enter the ${name} referral code on the ${name} signup form`,
        snapshotTitle: "Exchange Snapshot",
        snapshotSubtitle: "Service Type, Fees & Regulation",
        signupTitle: "Before You Sign Up",
        highlightsTitle: "Exchange Highlights",
        sidebarSummary: `The ${name} referral code is in the list.`,
        modalInstruction: "Copy the code below and paste it in the signup form, before you create your account.",
        modalVoteQuestion: "Did this code work?",
    };
}

export interface ReferralFaq { question: string; answer: string }

/**
 * The fixed FAQ of a REFERRAL page (owner 2026-11-04): every question targets "referral code"; at most one question also says
 * "promo code" and at most one says "coupon" (SEO template, section 3), and a template question is left out when the store's own
 * written FAQs already have one with that term (written FAQs are data and are shown as they are). No code, no bonus, nothing
 * about how codes are checked. The page appends these after the store's written FAQs; the FAQPage JSON-LD is built from the same list.
 */
export function referralFaqItems(name: string, written: ReadonlyArray<{ question: string }> = [], lateAdd?: string | null): ReferralFaq[] {
    const has = (term: RegExp) => written.some(item => term.test(item.question));
    const items: ReferralFaq[] = [
        {
            question: `Where do I enter the ${name} referral code?`,
            answer: `In the signup form, before your ${name} account is created. Copy the code from the list above, then paste it in the referral-code field.`,
        },
        {
            question: `Can I add a ${name} referral code after I sign up?`,
            answer: lateAdd?.trim() || `Usually not. The code goes in while you create the account, so use it at signup.`,
        },
        {
            question: `What if the ${name} referral code does not work?`,
            answer: `Check that you pasted it in the referral-code field before finishing signup, and that you are creating a new account. You can also tell us in the code popup by choosing "No" under "Did this code work?".`,
        },
    ];
    if (!has(/promo\s*code/i)) {
        items.push({
            question: `Is there a ${name} promo code besides the referral code?`,
            answer: `On this page the offer is a referral code. Copy it from the list above and paste it in the referral-code field of the signup form.`,
        });
    }
    if (!has(/coupon/i)) {
        items.push({
            question: `Is there a ${name} coupon besides the referral code?`,
            answer: `This page lists a referral code, not a coupon. It is entered at signup, not at a checkout.`,
        });
    }
    return items;
}

export interface LabelledRow { key: string; label: string; value: string }

type Attrs = Record<string, unknown> | null | undefined;
type ReferralFacts = {
    tradingFees?: string | null; listedCoins?: string | null; regulation?: string | null; depositMethods?: string | null;
    securityNote?: string | null; signupBenefit?: string | null; signupEntryPoint?: string | null; signupAudience?: string | null;
    attributes?: unknown;
};

const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
const attrText = (attrs: Attrs, key: string): string => text(attrs?.[key]);
const rows = (list: Array<[string, string, string]>): LabelledRow[] => list.filter(([, , value]) => value !== "").map(([key, label, value]) => ({ key, label, value }));

/** Boxes need two real rows, like the coupon page's Service Snapshot / Service Highlights (lib/service-snapshot). */
export const MIN_REFERRAL_ROWS = 2;

/** Exchange Snapshot table: Service Type, Trading Fees, Listed Coins, Regulation / Licence, Deposit Methods, Customer Support. */
export function referralSnapshotRows(store: ReferralFacts): LabelledRow[] {
    const attrs = store.attributes as Attrs;
    const all = rows([
        ["service_type", "Service Type", attrText(attrs, "core_identity")],
        ["trading_fees", "Trading Fees", text(store.tradingFees)],
        ["listed_coins", "Listed Coins", text(store.listedCoins)],
        ["regulation", "Regulation / Licence", text(store.regulation)],
        ["deposit_methods", "Deposit Methods", text(store.depositMethods)],
        ["support", "Customer Support", attrText(attrs, "support_type")],
    ]);
    return all.length >= MIN_REFERRAL_ROWS ? all : [];
}

/** Before You Sign Up: What you get, Where to enter it, Who it's for. Never a referral bonus and never a count. */
export function referralSignupRows(store: ReferralFacts): LabelledRow[] {
    return rows([
        ["what_you_get", "What you get", text(store.signupBenefit)],
        ["where_to_enter", "Where to enter it", text(store.signupEntryPoint)],
        ["who_for", "Who it's for", text(store.signupAudience)],
    ]);
}

/** Exchange Highlights: Best For, Security, Feature. */
export function referralHighlightRows(store: ReferralFacts): LabelledRow[] {
    const attrs = store.attributes as Attrs;
    const all = rows([
        ["best_for", "Best For", attrText(attrs, "user_level")],
        ["security", "Security", text(store.securityNote)],
        ["feature", "Feature", attrText(attrs, "killer_feature")],
    ]);
    return all.length >= MIN_REFERRAL_ROWS ? all : [];
}
